import camelcaseKeys from 'camelcase-keys'
import { create } from 'zustand'

import { executeGet, executePost } from '@/services/lukita'
import { createWithMutating } from '@/stores'
import { getUrl } from '@/utils/routing'
import type { CreditBank, CreditBankDTO, Wallet } from './types'

interface CreditBankStoreActions {
  setCreditBank: (
    wallet: Wallet,
    creditBank: CreditBank | CreditBankDTO,
  ) => void
  startLoading: (wallet: Wallet) => void
  stopLoading: (wallet: Wallet) => void
}

interface CreditBankStoreMutations {
  purchaseCredits: (priceId: string) => Promise<string>
}

interface CreditBankStoreLoaders {
  fetchCreditBank: (wallet: Wallet) => Promise<void>
}

interface CreditBankStore {
  creditBank: Record<Wallet, CreditBank | null>
  loadingCreditBank: Record<Wallet, boolean>
  mutating: boolean
  actions: CreditBankStoreActions
  mutations: CreditBankStoreMutations
  loaders: CreditBankStoreLoaders
}

export const useCreditBankStore = create<CreditBankStore>()((set, get) => {
  const withMutating = createWithMutating(set)

  return {
    creditBank: { pages: null, screenings: null },
    loadingCreditBank: { pages: true, screenings: true },
    mutating: false,

    actions: {
      setCreditBank: (wallet, creditBank) =>
        set((state) => ({
          creditBank: {
            ...state.creditBank,
            [wallet]: camelcaseKeys(creditBank, { deep: true }),
          },
        })),
      startLoading: (wallet) =>
        set((state) => ({
          loadingCreditBank: { ...state.loadingCreditBank, [wallet]: true },
        })),
      stopLoading: (wallet) =>
        set((state) => ({
          loadingCreditBank: { ...state.loadingCreditBank, [wallet]: false },
        })),
    },

    mutations: {
      purchaseCredits: (priceId) =>
        withMutating(async () => {
          const returnUrl = getUrl('credits')
          const response = await executePost('/credit_bank/purchase_credits', {
            price_id: priceId,
            success_url: returnUrl,
            cancel_url: returnUrl,
          })
          const account = await response.json()
          return account.checkout_url
        }),
    },

    loaders: {
      fetchCreditBank: async (wallet) => {
        const { actions } = get()
        actions.startLoading(wallet)
        const response = await executeGet(`/credit_bank`, {
          queryParams: { wallet },
        })
        const result = await response.json()
        actions.setCreditBank(wallet, result)
        actions.stopLoading(wallet)
      },
    },
  }
})

export const useCreditBank = (wallet: Wallet) =>
  useCreditBankStore((state) => state.creditBank[wallet])

export const useLoadingCreditBank = (wallet: Wallet) =>
  useCreditBankStore((state) => state.loadingCreditBank[wallet])

export const useCreditBankMutating = () =>
  useCreditBankStore((state) => state.mutating)

export const useCreditBankMutations = () =>
  useCreditBankStore((state) => state.mutations)

export const useCreditBankLoaders = () =>
  useCreditBankStore((state) => state.loaders)
